import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import axios from 'axios';
import { config } from '../../utils/utils';

type DayCell = {
  date: string;
  day: number;
  booked: number;
  remaining: number;
  capacity: number;
  fullyBooked: boolean;
};

type CapacityTone = 'available' | 'filling' | 'almostFull';

export type EventServiceProductOption = {
  id: string;
  labelEn: string;
  dailyCapacity?: number | null;
};

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const TONE_STYLES: Record<
  CapacityTone,
  {
    cardBg: string;
    border: string;
    bar: string;
    barTrack: string;
    pct: string;
    leftBg: string;
  }
> = {
  available: {
    cardBg: '#F3FBF4',
    border: 'rgba(76, 175, 80, 0.35)',
    bar: '#43A047',
    barTrack: 'rgba(67, 160, 71, 0.18)',
    pct: '#2E7D32',
    leftBg: 'rgba(67, 160, 71, 0.1)',
  },
  filling: {
    cardBg: '#FFF8F0',
    border: 'rgba(251, 140, 0, 0.4)',
    bar: '#FB8C00',
    barTrack: 'rgba(251, 140, 0, 0.18)',
    pct: '#EF6C00',
    leftBg: 'rgba(251, 140, 0, 0.1)',
  },
  almostFull: {
    cardBg: '#FFF5F5',
    border: 'rgba(229, 57, 53, 0.4)',
    bar: '#E53935',
    barTrack: 'rgba(229, 57, 53, 0.16)',
    pct: '#C62828',
    leftBg: 'rgba(229, 57, 53, 0.1)',
  },
};

const pad2 = (n: number) => String(n).padStart(2, '0');
const monthKey = (year: number, month: number) => `${year}-${pad2(month)}`;
const daysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

const getBookedPercent = (booked: number, capacity: number): number => {
  if (!capacity || capacity <= 0) return 0;
  return Math.min(100, Math.max(0, (booked / capacity) * 100));
};

const getCapacityTone = (
  booked: number,
  remaining: number,
  capacity: number
): CapacityTone => {
  if (remaining <= 0 && capacity > 0) return 'almostFull';
  const bookedPct = getBookedPercent(booked, capacity);
  if (bookedPct > 85) return 'almostFull';
  if (bookedPct >= 50) return 'filling';
  return 'available';
};

type Props = {
  productId: string;
  /** Rendered under title/stats (e.g. Delivery / Event Service tabs). */
  tabsAfterHeader?: ReactNode;
};

const EventServiceCapacityCalendar = ({
  productId,
  tabsAfterHeader,
}: Props) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [days, setDays] = useState<DayCell[]>([]);
  const [dailyCapacity, setDailyCapacity] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const requestIdRef = useRef(0);
  const [error, setError] = useState('');
  const [yearAnchorEl, setYearAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDay, setSelectedDay] = useState<DayCell | null>(null);

  const firstWeekday = useMemo(
    () => new Date(year, month - 1, 1).getDay(),
    [year, month]
  );

  const loadMonth = useCallback(async () => {
    if (!productId) {
      setDays([]);
      setDailyCapacity(null);
      setHasLoadedOnce(false);
      hasLoadedOnceRef.current = false;
      return;
    }
    const requestId = ++requestIdRef.current;
    // First paint only: full spinner. Product/month changes: keep grid + soft fade.
    const isFirstPaint = !hasLoadedOnceRef.current;
    if (isFirstPaint) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');
    try {
      const ym = monthKey(year, month);
      const { data } = await axios.get(
        `${config.BASE_URL}/event-service-product/${productId}/booked-dates`,
        { params: { month: ym } }
      );
      if (requestId !== requestIdRef.current) {
        return;
      }
      const capacity =
        data?.dailyCapacity == null ? null : Number(data.dailyCapacity);
      setDailyCapacity(capacity);

      const bookedMap = new Map<string, number>();
      for (const row of data?.bookedDates || []) {
        bookedMap.set(String(row.date), Number(row.qty) || 0);
      }

      const total = daysInMonth(year, month);
      const nextDays: DayCell[] = [];
      for (let day = 1; day <= total; day += 1) {
        const date = `${ym}-${pad2(day)}`;
        const booked = bookedMap.get(date) || 0;
        const dayCapacity = capacity == null ? 0 : capacity;
        const remaining =
          capacity == null ? 0 : Math.max(0, dayCapacity - booked);
        nextDays.push({
          date,
          day,
          booked,
          remaining,
          capacity: dayCapacity,
          fullyBooked: capacity != null && booked >= capacity,
        });
      }
      setDays(nextDays);
      hasLoadedOnceRef.current = true;
      setHasLoadedOnce(true);
      setSelectedDay(null);
    } catch (err: any) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to load capacity calendar'
      );
      if (!hasLoadedOnceRef.current) {
        setDays([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [productId, year, month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const cells = useMemo(() => {
    const blanks = Array.from({ length: firstWeekday }, () => null);
    const dates = Array.from(
      { length: daysInMonth(year, month) },
      (_, i) => i + 1
    );
    return [...blanks, ...dates];
  }, [firstWeekday, year, month]);

  const dayByNumber = useMemo(() => {
    const map = new Map<number, DayCell>();
    days.forEach((d) => map.set(d.day, d));
    return map;
  }, [days]);

  const monthStats = useMemo(() => {
    if (dailyCapacity == null) {
      const totalBooked = days.reduce((sum, d) => sum + d.booked, 0);
      return {
        totalCapacity: null as number | null,
        totalBooked,
        remaining: null as number | null,
        monthFill: 0,
      };
    }
    const totalCapacity = dailyCapacity * days.length;
    const totalBooked = days.reduce((sum, d) => sum + d.booked, 0);
    const remaining = Math.max(totalCapacity - totalBooked, 0);
    const monthFill = getBookedPercent(totalBooked, totalCapacity);
    return { totalCapacity, totalBooked, remaining, monthFill };
  }, [days, dailyCapacity]);

  const shiftMonth = (delta: number) => {
    const next = new Date(year, month - 1 + delta, 1);
    setYear(next.getFullYear());
    setMonth(next.getMonth() + 1);
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1.5,
          pr: 4.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.2rem' },
              color: '#1F2937',
              lineHeight: 1.25,
            }}
          >
            Capacity Calendar
          </Typography>
          <Typography sx={{ mt: 0.3, fontSize: '0.78rem', color: '#9CA3AF' }}>
            Daily event service availability
          </Typography>
        </Box>

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          justifyContent='center'
          flexWrap='nowrap'
        >
          {[
            {
              label: 'TOTAL CAPACITY',
              value:
                monthStats.totalCapacity == null
                  ? '∞'
                  : monthStats.totalCapacity.toLocaleString(),
              valueColor: '#1E293B',
            },
            {
              label: 'TOTAL BOOKED',
              value: monthStats.totalBooked.toLocaleString(),
              valueColor: '#CA7A1C',
            },
            {
              label: 'REMAINING',
              value:
                monthStats.remaining == null
                  ? '∞'
                  : monthStats.remaining.toLocaleString(),
              valueColor: '#10B981',
            },
            {
              label: 'MONTH FILL',
              value:
                monthStats.totalCapacity == null
                  ? '—'
                  : `${Math.round(monthStats.monthFill)}%`,
              valueColor: '#10B981',
            },
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                px: 1.5,
                py: 0.9,
                borderRadius: 2,
                bgcolor: '#F4F0EB',
                minWidth: 108,
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#5E6D82',
                  letterSpacing: 0.4,
                  lineHeight: 1.2,
                  mb: 0.35,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: stat.valueColor,
                  lineHeight: 1.15,
                }}
              >
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      <Divider sx={{ mb: tabsAfterHeader ? 1.5 : 2 }} />

      {tabsAfterHeader ? <Box sx={{ mb: 2 }}>{tabsAfterHeader}</Box> : null}

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='center'
        spacing={1}
        sx={{ mb: 1.25 }}
      >
        <IconButton
          size='small'
          onClick={() => shiftMonth(-1)}
          aria-label='Previous month'
        >
          <ChevronLeftIcon />
        </IconButton>
        <Box
          onClick={(e) => setYearAnchorEl(e.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            minWidth: 140,
            justifyContent: 'center',
            userSelect: 'none',
            '&:hover': { opacity: 0.8 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#2F2A27',
              mr: 0.25,
            }}
          >
            {MONTHS[month - 1]} {year}
          </Typography>
          {yearAnchorEl ? (
            <ArrowDropUpIcon sx={{ color: '#2F2A27', fontSize: '1.25rem' }} />
          ) : (
            <ArrowDropDownIcon sx={{ color: '#2F2A27', fontSize: '1.25rem' }} />
          )}
        </Box>
        <IconButton
          size='small'
          onClick={() => shiftMonth(1)}
          aria-label='Next month'
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      <Popover
        open={Boolean(yearAnchorEl)}
        anchorEl={yearAnchorEl}
        onClose={() => setYearAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            p: 2,
            width: 280,
            borderRadius: '12px',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.05)',
            mt: 0.75,
          },
        }}
      >
        <Box
          sx={{
            maxHeight: 240,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1.25,
          }}
        >
          {Array.from({ length: 31 }, (_, i) => 2015 + i).map((y) => {
            const isActive = y === year;
            return (
              <Box
                key={y}
                onClick={() => {
                  setYear(y);
                  setYearAnchorEl(null);
                }}
                sx={{
                  py: 1,
                  textAlign: 'center',
                  borderRadius: '20px',
                  fontWeight: isActive ? 700 : 600,
                  fontSize: '0.95rem',
                  bgcolor: isActive ? '#EE3750' : 'transparent',
                  color: isActive ? '#FFFFFF' : '#2F2A27',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: isActive ? '#EE3750' : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                {y}
              </Box>
            );
          })}
        </Box>
      </Popover>

      <Stack
        direction='row'
        spacing={2}
        justifyContent='flex-start'
        flexWrap='wrap'
        useFlexGap
        sx={{ mb: 1.5 }}
      >
        {[
          { tone: 'available' as const, label: 'Available (<50%)' },
          { tone: 'filling' as const, label: 'Filling Up (50–85%)' },
          { tone: 'almostFull' as const, label: 'Almost Full (>85%)' },
        ].map((item) => (
          <Box
            key={item.tone}
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: TONE_STYLES[item.tone].bar,
              }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: '#7A7470' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      {loading && !hasLoadedOnce ? (
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            minHeight: { xs: 320, sm: 440 },
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : error && !hasLoadedOnce ? (
        <Typography color='error'>{error}</Typography>
      ) : !productId ? (
        <Typography color='text.secondary'>
          Select an event service to view capacity.
        </Typography>
      ) : (
        <Box
          sx={{
            position: 'relative',
            minWidth: { xs: 560, sm: 0 },
            overflowX: 'auto',
            opacity: refreshing ? 0.55 : 1,
            transition: 'opacity 0.2s ease',
            pointerEvents: refreshing ? 'none' : 'auto',
          }}
        >
          {refreshing ? (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
                display: 'grid',
                placeItems: 'center',
                width: 36,
                height: 36,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.9)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
              }}
            >
              <CircularProgress size={18} sx={{ color: '#EE3750' }} />
            </Box>
          ) : null}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.75,
              mb: 0.75,
            }}
          >
            {WEEKDAYS.map((day) => (
              <Box
                key={day}
                sx={{
                  py: 0.9,
                  textAlign: 'center',
                  borderRadius: 999,
                  bgcolor: 'rgba(180, 145, 127, 0.12)',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 11,
                    color: '#EE3750',
                    letterSpacing: 0.4,
                  }}
                >
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.75,
            }}
          >
            {cells.map((dayNumber, index) => {
              const summary = dayNumber ? dayByNumber.get(dayNumber) : null;
              const unlimited = dailyCapacity == null;
              const tone = summary
                ? getCapacityTone(
                    summary.booked,
                    summary.remaining,
                    summary.capacity || 1
                  )
                : 'available';
              const styles = TONE_STYLES[tone];
              const bookedPct =
                summary && !unlimited
                  ? Math.round(
                      getBookedPercent(summary.booked, summary.capacity)
                    )
                  : 0;
              const isToday =
                dayNumber != null &&
                year === now.getFullYear() &&
                month === now.getMonth() + 1 &&
                dayNumber === now.getDate();
              const isSelected = selectedDay?.day === dayNumber;

              return (
                <Box
                  key={`cell-${index}`}
                  sx={{
                    minHeight: { xs: 98, sm: 108 },
                    borderRadius: 2.25,
                    border: '1.5px solid',
                    borderColor: !dayNumber
                      ? 'transparent'
                      : isToday
                        ? '#EE3750'
                        : isSelected
                          ? '#3D342F'
                          : styles.border,
                    p: { xs: 0.65, sm: 0.8 },
                    bgcolor: !dayNumber ? 'transparent' : styles.cardBg,
                    cursor: summary ? 'pointer' : 'default',
                    boxShadow: isToday
                      ? '0 0 0 1px #EE3750'
                      : isSelected
                        ? '0 0 0 1px #3D342F'
                        : 'none',
                    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                    '&:hover': summary
                      ? {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(33, 43, 54, 0.1)',
                        }
                      : undefined,
                  }}
                  onClick={() => {
                    if (summary) setSelectedDay(summary);
                  }}
                >
                  {dayNumber && summary ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 0.55,
                        }}
                      >
                        {isToday ? (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: '#EE3750',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#FFFFFF',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            {dayNumber}
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: 12, md: 13 },
                              color: '#2F2A27',
                            }}
                          >
                            {dayNumber}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: { xs: 10, md: 11 },
                            color: styles.pct,
                          }}
                        >
                          {unlimited ? 'Open' : `${bookedPct}%`}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          bgcolor: styles.barTrack,
                          overflow: 'hidden',
                          mb: 0.6,
                          border: '1px solid',
                          borderColor: 'rgba(33, 43, 54, 0.2)',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${Math.max(
                              bookedPct,
                              bookedPct > 0 ? 3 : 0
                            )}%`,
                            height: '100%',
                            bgcolor: styles.bar,
                            borderRadius: 999,
                            transition: 'width 0.25s ease',
                          }}
                        />
                      </Box>

                      <Stack spacing={0.25} sx={{ flex: 1 }}>
                        {[
                          {
                            label: 'Total',
                            value: unlimited ? '∞' : summary.capacity,
                          },
                          { label: 'Booked', value: summary.booked },
                        ].map((row) => (
                          <Box
                            key={row.label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 10,
                                color: '#7A7470',
                                fontWeight: 600,
                              }}
                            >
                              {row.label}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 11,
                                color: '#2F2A27',
                                fontWeight: 700,
                              }}
                            >
                              {row.value}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Box
                        sx={{
                          mt: 0.45,
                          px: 0.65,
                          py: 0.3,
                          borderRadius: 1.25,
                          bgcolor: styles.leftBg,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: styles.pct,
                          }}
                        >
                          Left
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            color: styles.pct,
                          }}
                        >
                          {unlimited ? '∞' : summary.remaining}
                        </Typography>
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EventServiceCapacityCalendar;
